import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { motion } from 'framer-motion';

import { apiClient } from '../../core/api/client';
import type { WalletSnapshot } from '../../core/api/types';
import { formatUnits } from '../../core/money';
import { useMotionPresets } from '../../design/motion';
import {
  formatPlanPrice,
  listPublicPlans,
  purchasePlanWithBalance,
  quotaLabel,
} from './api';
import type { ServerPlan } from './api';
import { loadEntitlements, useEntitlements } from './entitlements';
import { loadSelection, selectByo } from './persist';
import type { SubscriptionSelection } from './persist';
import styles from './subscription.module.css';

const CAPABILITY_LABELS = {
  autoText: '自动生文',
  autoImage: '自动生图',
  autoTts: '自动语音',
} as const;

export interface PlanSelectionPageProps {
  onClose?: () => void;
  initialSelection?: SubscriptionSelection;
  onSelectionChange?: (selection: SubscriptionSelection) => void;
}

function capabilityText(plan: ServerPlan): string {
  return (Object.entries(CAPABILITY_LABELS) as [keyof typeof CAPABILITY_LABELS, string][])
    .filter(([key]) => plan.capabilities[key])
    .map(([, label]) => label)
    .join(' · ') || '基础服务';
}

export function PlanSelectionPage({
  onClose,
  initialSelection,
  onSelectionChange,
}: PlanSelectionPageProps): ReactElement {
  const motionPresets = useMotionPresets();
  const entitlements = useEntitlements();
  const [plans, setPlans] = useState<ServerPlan[]>([]);
  const [selection, setSelection] = useState<SubscriptionSelection>(() => initialSelection ?? loadSelection());
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<{ plan: ServerPlan; key: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [available, balance] = await Promise.allSettled([
        listPublicPlans(),
        apiClient.wallet(),
        loadEntitlements(true),
      ]);
      if (available.status === 'fulfilled') setPlans(available.value);
      else setError(available.reason instanceof Error ? available.reason.message : '套餐加载失败');
      if (balance.status === 'fulfilled') setWallet(balance.value);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const chooseByo = (): void => {
    const next = selectByo();
    setSelection(next);
    onSelectionChange?.(next);
  };

  const confirmPurchase = async (): Promise<void> => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await purchasePlanWithBalance(pending.plan.id, pending.key);
      setWallet((current) => current ? { ...current, balanceUnits: result.balanceUnits } : current);
      setNotice(`已购买「${pending.plan.name}」，有效期至 ${new Date(result.subscription.expiresAt).toLocaleString()}`);
      setPending(null);
      await loadEntitlements(true);
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '购买失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="选择套餐"
      className={styles.page}
      data-skin-surface="page"
      variants={motionPresets.sheet}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <button type="button" className={styles.back} aria-label="返回设置" onClick={() => onClose?.()}>‹</button>
      <header className={styles.planHeader}>
        <div>
          <h1>官方订阅</h1>
          <p>套餐由管理员动态配置；余额购买后立即生效。</p>
        </div>
        <span className={styles.planBalance}>{wallet ? `${formatUnits(wallet.balanceUnits)} ${wallet.unit}` : '余额加载中'}</span>
      </header>

      <div className={styles.planScroll}>
        {entitlements.active ? (
          <section className={styles.planActive} aria-label="当前套餐">
            <strong>{entitlements.subscriptions.map((subscription) => subscription.planName).join(' + ') || '官方套餐'}</strong>
            <span>有效至 {entitlements.expiresAt ? new Date(entitlements.expiresAt).toLocaleString() : '未知'}</span>
            <small>文字额度 {quotaLabel(entitlements.quota.textTokens)} · 图片额度 {quotaLabel(entitlements.quota.images)} · 语音额度 {quotaLabel(entitlements.quota.ttsChars)}</small>
          </section>
        ) : (
          <section className={styles.planUnavailable} aria-label="当前套餐">
            当前没有有效官方套餐：可正常聊天，但只能使用「仅回复消息」自动化，自动生图与自动语音不可启用。
          </section>
        )}

        {loading ? <p className={styles.notice}>正在加载套餐…</p> : null}
        {!loading && plans.length === 0 ? <p className={styles.notice}>管理员暂未上架套餐。</p> : null}

        <div className={styles.planList} aria-label="官方套餐列表">
          {plans.map((plan) => (
            <article key={plan.id} className={styles.planCard} data-ui-id={`subscription.plan.${plan.id}`}>
              <div className={styles.planCardTop}>
                <div>
                  <h2>{plan.name}</h2>
                  <p>{plan.summary || '官方订阅套餐'}</p>
                </div>
                <div className={styles.planPrice}>
                  <strong>{formatPlanPrice(plan.priceCents)}</strong>
                  <span>{plan.durationDays} 天</span>
                </div>
              </div>
              <div className={styles.planTags}>
                {plan.tags.map((tag) => <span key={tag}>{tag}</span>)}
              </div>
              <p className={styles.planCapabilities}>{capabilityText(plan)}</p>
              {plan.description ? <p className={styles.planDescription}>{plan.description}</p> : null}
              {plan.quotaEnabled ? (
                <p className={styles.planQuota}>
                  每月额度：文字 {plan.textTokenQuota.toLocaleString()} token · 图片 {plan.imageQuota.toLocaleString()} 张 · 语音 {plan.ttsCharQuota.toLocaleString()} 字符
                </p>
              ) : <p className={styles.planQuota}>套餐额度：不限量（受服务速率限制）</p>}
              <button
                type="button"
                className={styles.planBuy}
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setNotice(null);
                  setPending({ plan, key: crypto.randomUUID() });
                }}
              >余额购买</button>
            </article>
          ))}
        </div>

        <button
          type="button"
          className={selection.mode === 'byo' ? `${styles.byoCard} ${styles.byoCardActive}` : styles.byoCard}
          aria-pressed={selection.mode === 'byo'}
          onClick={chooseByo}
        >
          <strong>不使用官方套餐，自行配置模型</strong>
          <span>自行配置文字 / 生图 / TTS 提供商。自动生图、自动语音和定时自动化仍需有效套餐权限。</span>
        </button>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        {notice ? <p className={styles.success} role="status">{notice}</p> : null}
      </div>

      {pending ? (
        <>
          <div className={styles.backdrop} aria-hidden="true" onClick={() => { if (!busy) setPending(null); }} />
          <section className={styles.confirmDialog} role="dialog" aria-modal="true" aria-label="确认购买套餐">
            <h2>确认购买{pending.plan.name}</h2>
            <p>将从栀籽余额扣除 {formatPlanPrice(pending.plan.priceCents)}。同类套餐会顺延续期；不同套餐可并行生效。</p>
            <p className={styles.muted}>当前余额：{wallet ? `${formatUnits(wallet.balanceUnits)} ${wallet.unit}` : '加载中'}</p>
            <div className={styles.confirmActions}>
              <button type="button" disabled={busy} onClick={() => setPending(null)}>取消</button>
              <button type="button" className={styles.primary} disabled={busy} onClick={() => void confirmPurchase()}>{busy ? '处理中…' : '确认扣款'}</button>
            </div>
          </section>
        </>
      ) : null}
    </motion.div>
  );
}
